const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mBlock.tsx';
import * as React from 'react';
import { forwardRef, useMemo } from 'react';
import { css, cx } from '@emotion/css';
import { useConnectToChildCIS4mComponents } from '../hooks';
import { CIS4mTreeComponentProps } from '../types';
import { cssInvisible } from '../styles';
import { useTheme } from '@mui/material/styles';
import { KeyValuePair } from '@cistec/common_web/defs/generalDefs';

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            cssBlock: css`
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                margin-top: ${theme.spacing(0.5)};
                margin-bottom: ${theme.spacing(0.5)};

                &.disabled {
                    opacity: 0.3;

                    & .disabled {
                        opacity: 1;
                    }
                }

                &.flex {
                    display: flex;
                    flex-direction: row;
                    gap: ${theme.spacing(1)};

                    & > * {
                        margin-top: 0;
                    }
                }

                &.flex.left {
                    & > * {
                        flex: none;
                    }
                    & > *:first-child {
                        flex: auto;
                        width: auto;
                    }
                }

                &.flex.right {
                    & > * {
                        flex: none;
                    }
                    & > *:last-child {
                        flex: auto;
                        width: auto;
                    }
                }

                &.flex.auto {
                    & > * {
                        flex: auto;
                    }
                }
            `,
            cssSpaceBetween: css`
                display: flex !important;
                justify-content: space-between;
                width: 100%;

                & > * {
                    margin-top: 0 !important;
                }
            `,

            cssMarginBottom: css`
                margin-bottom: ${theme.spacing(2)};
            `,
        }),
        [theme]
    );
}

interface Props extends CIS4mTreeComponentProps {
    children: any;
    spaceBetween?: true;
    hasMarginBottom?: boolean;
    className?: string;
    flex?: 'left' | 'right' | 'auto';
}

export const CIS4mBlock = forwardRef((props: Props, ref: any) => {
    const { children, disabled = false, spaceBetween, hidden = false, hasMarginBottom, className, flex } = props;
    const { cssBlock, cssSpaceBetween, cssMarginBottom } = useCSSClasses();

    const propsToAdd: CIS4mTreeComponentProps = {};
    if (disabled) propsToAdd.disabled = true;
    if (hidden) propsToAdd.hidden = true;

    const referencedChildren = useConnectToChildCIS4mComponents(ref, children, undefined, propsToAdd);

    const flexClasses: KeyValuePair = {};
    if (flex) {
        flexClasses.flex = true;
        flexClasses[flex] = true;
    }

    return (
        <div
            className={cx('MobileFormBlock', cssBlock, className, {
                [cssMarginBottom]: hasMarginBottom,
                [cssSpaceBetween]: spaceBetween,
                [cssInvisible]: hidden,
                hidden,
                ...flexClasses,
            })}
        >
            {referencedChildren}
        </div>
    );
});

CIS4mBlock.displayName = 'CIS4mBlock';
